import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Stack, IconButton } from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  RemoveCircleOutlined as DeleteIcon,
} from '@mui/icons-material';

import BarrierFields from './components/BarrierFields';
import { FHButton, FProgressBar } from '../../../../../../../base';
import { assessmentActions } from '../../../../../../../../store';
import { useTreatmentPlanEditable } from '../../../../../hooks/useTreatmentPlanEditable';
import { Themes } from '../../../../../../../../constants';
import { FORTA_WHITE, FORTA_BLUE } from '../../../../../../../../utils/colors';
import { BARRIER_OPTIONS } from '../../../../constants/general';
import { ERROR_STATES, STATE_IDS } from '../../../../constants/states';

import type { IOption } from '../../../../../../../../utils/types';

function AssesmentBarriers() {
  const editable = useTreatmentPlanEditable();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { assessment, barriers, barriersLoading } = useSelector((state: any) => state.assessments);
  const planId = treatmentPlan.id;
  const assessmentId = assessment.id;

  const [filteredBarrierOptions, setFilteredBarrierOptions] = useState<Array<IOption>>([]);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [barrierCount, setBarrierCount] = useState<number>(0);

  useEffect(() => {
    const barrierlabels = barriers.map((br: IOption) => br.label);
    const filteredOptions = BARRIER_OPTIONS.filter(
      (br: IOption) => !barrierlabels.includes(br.label),
    );
    setFilteredBarrierOptions(filteredOptions);
    if (barriers.length > barrierCount) {
      setIsAdding(false);
    }
    setBarrierCount(barriers.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barriers]);

  const handleDeleteNewBarrier = () => {
    setIsAdding(false);
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [`${ERROR_STATES[STATE_IDS.ASSESSMENT_BARRIER]}-new`]: 0,
        },
      }),
    );
  };

  const handleBarrierDelete = (id: number) => {
    if (!id) return;
    dispatch(
      assessmentActions.deleteBarrier({
        getAccessTokenSilently,
        planId,
        assessmentId,
        barrierId: id,
      }),
    );
  };

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Box sx={{ width: 200 }}>
        {editable && (
          <FHButton
            fullWidth
            disabled={isAdding || barriersLoading}
            colorTheme={Themes.Primary}
            onClick={() => setIsAdding(true)}
            startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
          >
            Add Barrier
          </FHButton>
        )}
      </Box>
      <Stack spacing={2} ml={2} mb={2} sx={{ flexGrow: 1 }}>
        {isAdding && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box width={35} mt={1}>
              <IconButton sx={{ p: 0, m: 0 }} disabled={!editable} onClick={handleDeleteNewBarrier}>
                <DeleteIcon sx={{ color: FORTA_BLUE.primary }} />
              </IconButton>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <BarrierFields filteredBarrierOptions={filteredBarrierOptions} />
            </Box>
          </Box>
        )}
        {barriers.map((br: IOption) => (
          <Box key={br.id} sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box width={35} mt={1}>
              {editable && (
                <IconButton
                  sx={{ p: 0, m: 0 }}
                  disabled={!editable || barriersLoading}
                  onClick={() => {
                    const barrierId = Number(br.id);
                    if (Number.isInteger(barrierId)) handleBarrierDelete(barrierId);
                  }}
                >
                  <DeleteIcon sx={{ color: FORTA_BLUE.primary }} />
                </IconButton>
              )}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <BarrierFields barrier={br} filteredBarrierOptions={filteredBarrierOptions} />
            </Box>
          </Box>
        ))}
      </Stack>
      {barriersLoading && <FProgressBar />}
    </Box>
  );
}

export default AssesmentBarriers;
